import { useEffect, useSyncExternalStore } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { bibleApi, booksApi, verseApi, commentApi, highlightApi } from '../services/api';
import { getContentVersion, subscribeContentVersion } from '../services/contentVersion';
import { groupByVerse } from '../services/highlights';
import type { VerseHighlights } from '../types';

// Single source of truth for the per-panel content keys, so the component queries below and the
// startup prefetch stay byte-for-byte identical (React Query dedupes only on an exact key match).
// The content version is part of the key: when an import or deletion moves it, these become different
// queries and refetch, which is what replaces invalidating the caches sitting in front of the API.
export const versesKey = (bibleId: number, bookId: number, version: string) =>
  ['verses', bibleId, bookId, version] as const;
export const chaptersKey = (bibleId: number, bookId: number, version: string) =>
  ['chapters', bibleId, bookId, version] as const;
export const availableBooksKey = (bibleId: number, version: string) =>
  ['availableBooks', bibleId, version] as const;
export const commentCountsKey = (bookId: number, chapter: number) =>
  ['commentCounts', bookId, chapter] as const;
// Per-user annotation keys. The user id is part of the key so one account's cache is never read as another's.
export const highlightsKey = (userId: number, bibleId: number, bookId: number, chapter: number) =>
  ['highlights', userId, bibleId, bookId, chapter] as const;
export const bookmarksKey = (userId: number) =>
  ['bookmarks', userId] as const;

export async function fetchChapterHighlights(bibleId: number, bookId: number, chapter: number): Promise<VerseHighlights> {
  return groupByVerse(await highlightApi.getByChapter(bibleId, bookId, chapter));
}

/**
 * The content version last seen from the API, re-rendering subscribers when it moves.
 */
export function useContentVersion(): string {
  return useSyncExternalStore(subscribeContentVersion, getContentVersion);
}

/**
 * Opens every request a panel needs without waiting for the panel to exist. BiblePanel's own queries
 * dedupe onto these by key, so whichever code learns the ids first can start the network work.
 */
export function prefetchPanelData(
  queryClient: QueryClient,
  bibleId: number,
  bookId: number,
  version: string,
) {
  if (bibleId <= 0 || bookId <= 0) return;

  queryClient.prefetchQuery({
    queryKey: versesKey(bibleId, bookId, version),
    queryFn: () => verseApi.getByBook(bibleId, bookId, version),
  });
  queryClient.prefetchQuery({
    queryKey: chaptersKey(bibleId, bookId, version),
    queryFn: () => verseApi.getChapters(bibleId, bookId, version),
  });
  queryClient.prefetchQuery({
    queryKey: availableBooksKey(bibleId, version),
    queryFn: () => verseApi.getBooksForBible(bibleId, version),
  });
}

export function useBiblesQuery() {
  return useQuery({
    queryKey: ['bibles'],
    queryFn: bibleApi.getAll,
    staleTime: 5 * 60 * 1000,
  });
}

export function useBooksQuery() {
  return useQuery({
    queryKey: ['books'],
    queryFn: booksApi.getAll,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAvailableBooksQuery(bibleId: number) {
  const version = useContentVersion();
  return useQuery({
    queryKey: availableBooksKey(bibleId, version),
    queryFn: () => verseApi.getBooksForBible(bibleId, version),
    enabled: bibleId > 0,
    staleTime: 5 * 60 * 1000,
  });
}

export function useChaptersQuery(bibleId: number, bookId: number) {
  const version = useContentVersion();
  return useQuery({
    queryKey: chaptersKey(bibleId, bookId, version),
    queryFn: () => verseApi.getChapters(bibleId, bookId, version),
    enabled: bibleId > 0 && bookId > 0,
  });
}

export function useVersesQuery(bibleId: number, bookId: number) {
  const version = useContentVersion();
  return useQuery({
    queryKey: versesKey(bibleId, bookId, version),
    queryFn: () => verseApi.getByBook(bibleId, bookId, version),
    enabled: bibleId > 0 && bookId > 0,
  });
}

/**
 * Warm the verse/chapter caches for panels restored from localStorage, at mount, in parallel with
 * the bibles/books requests. Without this the queries only start once BookView's loading gate mounts
 * BiblePanel (after bibles+books resolve), producing a second sequential request wave. React Query
 * dedupes on the shared keys, so BiblePanel later reuses these in-flight requests instead of refiring.
 *
 * The version used here is the one remembered from the previous visit, which is what lets these
 * requests leave before the API has said anything. If content changed in the meantime, the response to
 * /bibles carries the new version, the keys move with it and these refetch — one wasted pair of
 * requests on the visit after a change, in exchange for pinned URLs on every other visit.
 */
export function usePrefetchPanels(panels: ReadonlyArray<{ bibleId: number; canonicalBookId: number }>) {
  const queryClient = useQueryClient();
  const version = useContentVersion();
  useEffect(() => {
    panels.forEach(({ bibleId, canonicalBookId }) => {
      prefetchPanelData(queryClient, bibleId, canonicalBookId, version);
    });
    // Startup-only optimization for panels already known from localStorage; must not refire on every
    // selection change (BiblePanel's own queries own the steady state after mount).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);
}

export function useCommentsQuery(bookId: number, chapter: number, verse: number, enabled: boolean) {
  return useQuery({
    queryKey: ['comments', bookId, chapter, verse],
    queryFn: () => commentApi.getByVerse(bookId, chapter, verse),
    enabled,
  });
}

/**
 * The comment counts of the chapter on screen, so verses carrying a comment can be marked without
 * asking the API about each verse in turn. Not part of the content-version keys: comments change
 * independently of the imported text, and the mutations invalidate this key directly.
 */
export function useCommentCountsQuery(bookId: number, chapter: number) {
  return useQuery({
    queryKey: commentCountsKey(bookId, chapter),
    queryFn: () => commentApi.getCountsByChapter(bookId, chapter),
    enabled: bookId > 0 && chapter > 0,
  });
}

export function useSearchQuery(query: string) {
  return useQuery({
    queryKey: ['search', query],
    queryFn: () => verseApi.search(query),
    enabled: !!query.trim(),
  });
}
